import Pallet from "./Pallet";
import HttpClient from "./HttpClient";
import Order from "./Order.js";
import Vehicle from "./Vehicle";
import Helper from "./Helper.js";

export default class Truck {
  constructor(truck, searchedPallets) {
    if (!truck) return;
    this.id = truck.id;
    this.description = truck.description;
    this.orders = truck.orders;
    this.delivery_date = truck.delivery_date;
    this.company_id = truck.company_id;
    this.created_at = truck.created_at;
    this.updated_at = truck.updated_at;
    this.status = truck.status;
    this.user_id = truck.user_id;
    this.customers = truck.customers;
    this.locked_by = truck.locked_by;
    this.locked_until = truck.locked_until;
    this.width = truck.width;
    this.length = truck.length;
    this.height = truck.height;
    this.max_pallet_weight = truck.max_pallet_weight;
    this.vehicle_id = truck.vehicle_id;
    this.allow_turn = truck.allow_turn;
    this.pallets = (truck.pallets) ? this.getPallets(truck.pallets, searchedPallets) : [];
    this.pallets_count = truck.pallets_count;
    this.products_count = truck.products_count;
    this.between_pallets = truck.between_pallets;
    this.vehicle = new Vehicle(truck.vehicle);
  }

  static async loadTruck(truckId, params) {
    const client = new HttpClient();
    const truckResponse = await client.instance.get(`trucks/${truckId}`, {
      params: params
    });
    return new Truck(truckResponse.data);
  }

  static async loadTrucks(params) {
    const client = new HttpClient();
    const truckResponse = await client.instance.get("trucks", {
      params: params
    });
    let trucks = [];
    truckResponse.data.data.forEach(truck => {
      trucks.push(new Truck(truck));
    });
    const total = truckResponse.data.total;
    const page = truckResponse.data.current_page;
    const pages = truckResponse.data.last_page;
    return { page, total, pages, trucks };
  }

  toString() {
    // const description = this.description ? `${this.description} ` : "";
    const length = this.length ? this.length : "";
    const width = this.width ? this.width : "";
    const height = this.height ? this.height : "";
    return `${width} x ${length}, H=${height}`;
  }

  async save() {
    const client = new HttpClient();
    const response = await client.instance.put(`trucks/${this.id}`, this);
    return response.data;
  }

  async optimize(pallets) {
    const client = new HttpClient();
    const response = await client.instance.post(`trucks/${this.id}/optimize`, {
      pallets: pallets
    });
    return response.data;
  }

  async remove() {
    const client = new HttpClient();
    await client.instance.delete(`trucks/${this.id}`);
  }

  getPallets(pallets, searchedPallets) {
    let palletObjects = [];
    for (let pallet of pallets) {
      if (pallet.searched === false) {
        palletObjects.push(new Pallet(pallet));
        continue;
      }
      pallet.searched = true;
      if (searchedPallets) {
        const found = searchedPallets.map(searchedPallet => { return searchedPallet.id }).indexOf(pallet.id);
        if (found === -1) {
          pallet.searched = false;
        }
      }
      palletObjects.push(new Pallet(pallet));
    }
    return palletObjects;
  }

  static async generate(pallets, settings) {
    const client = new HttpClient();
    const unloadedPallets = pallets.filter(pallet => {
      return pallet.truck_id == null;
    });
    const orders = Order.getOrdersFromPallets(pallets);
    const truckSettings = {
      orders: Order.getOrdersString(orders),
      customers: Order.getCustomersString(orders),
      width: settings.truck_width,
      height: settings.truck_height,
      length: settings.truck_length,
      max_pallet_weight: settings.max_weight,
      between_pallets: settings.default_truck_spacing,
      status: 1
    };
    //create truck to which to add pallets to
    const truckResponse = await client.instance.post("trucks", truckSettings);
    const truck = new Truck(truckResponse.data);
    const optimizationResponse = await truck.optimize(unloadedPallets);
    return optimizationResponse;
  }

  getLength() {
    let length = 0;
    this.pallets.forEach(pallet => {
      length = Math.max(length, pallet.truck_x + pallet.getLength());
    });
    return length;
  }

  getHeight() {
    let height = 0;
    this.pallets.forEach(pallet => {
      height = Math.max(height, pallet.getHeight());
    });
    return height;
  }

  getMaxPalletWeight() {
    let maxWeight = 0;
    this.pallets.forEach(pallet => {
      maxWeight = Math.max(maxWeight, pallet.getWeight());
    });
    return maxWeight;
  }

  prepareForViewing(divider) {
    this.width /= divider;
    this.length /= divider;
    this.height /= divider;
    this.divider = divider;
    this.pallets.forEach(pallet => {
      pallet.truck_x /= divider;
      pallet.truck_y /= divider;
      pallet.length /= divider;
      pallet.width /= divider;
      pallet.pallet_height /= divider;
      pallet.spacing_between /= divider;
      pallet.spacing_ends /= divider;
      pallet.spacing_sides /= divider;
      pallet.max_weight /= divider;
      pallet.max_height /= divider;
      pallet.divider = divider;
      pallet.products.forEach(product => {
        product.x /= divider;
        product.y /= divider;
        product.z /= divider;
        product.width /= divider;
        product.height /= divider;
        product.depth /= divider;
      })
    });
    return this;
  }

  prepareForSave() {
    const truck = new Truck(this);
    const divider = this.divider;
    truck.width = Math.round(truck.width * divider);
    truck.length = Math.round(truck.length * divider);
    truck.height = Math.round(truck.length * divider);
    truck.pallets.forEach(pallet => {
      pallet.truck_x = Math.round(pallet.truck_x * divider);
      pallet.truck_y = Math.round(pallet.truck_y * divider);
      pallet.length = Math.round(pallet.length * divider);
      pallet.width = Math.round(pallet.width * divider);
      pallet.pallet_height = Math.round(pallet.pallet_height * divider);
      pallet.spacing_between = Math.round(pallet.spacing_between * divider);
      pallet.spacing_ends = Math.round(pallet.spacing_ends * divider);
      pallet.spacing_sides = Math.round(pallet.spacing_sides * divider);
      pallet.max_weight = Math.round(pallet.max_weight * divider);
      pallet.max_height = Math.round(pallet.max_height * divider);
      pallet.products.forEach(product => {
        product.x = Math.round(product.x * divider);
        product.y = Math.round(product.y * divider);
        product.z = Math.round(product.z * divider);
        product.width = Math.round(product.width * divider);
        product.height = Math.round(product.height * divider);
        product.depth = Math.round(product.depth * divider);
      })
    });
    return truck;
  }

  overPallet(event) {
    const mousePos = {
      x: event.offsetX,
      y: event.offsetY
    };
    let selectedPallet = null;
    this.pallets.forEach(pallet => {
      if (
        mousePos.x > pallet.truck_x &&
        mousePos.x < pallet.truck_x + pallet.getLength() &&
        mousePos.y > pallet.truck_y &&
        mousePos.y < pallet.truck_y + pallet.getWidth()
      ) {
        selectedPallet = pallet;
      }
    })
    return selectedPallet;
  }

  palletsFit() {
    if (!this.withinBounds()) {
      return false;
    }
    if (this.intersectingPallets()) {
      return false
    }
    return true;
  }

  withinBounds() {
    for (const pallet of this.pallets) {
      //check if pallet within bounds of truck
      if (!pallet.fits(this)) {
        return false;
      }
    }
    return true;
  }

  intersectingPallets() {
    for (let i = 0; i < this.pallets.length; i++) {
      for (let j = 0; j < this.pallets.length; j++) {
        if (this.pallets[i].id != this.pallets[j].id) {
          if (this.pallets[i].intersects(this.pallets[j])) {
            return true;
          }
        }
      }
    }
    return false;
  }

  async rearrangePallets() {
    // const pallets = Pallet.copyPallets(this.pallets);
    const pallets = this.pallets.slice();
    //first sort pallets by truck_x coordinate
    //so that we start from left to right
    pallets.sort((a, b) => {
      return a.truck_x - b.truck_x;
    });
    //move each pallet left as much as possible
    const movedLeft = []; //save pallets that have moved
    for (const pallet of pallets) {
      pallet.moveLeft(movedLeft, this.between_pallets);
      movedLeft.push(pallet);
      await Helper.timeout(50);
      document.dispatchEvent(new Event("moved"));
    }
    //then sort pallets by truck_y coordinate
    //so that we start from top to bottom
    pallets.sort((a, b) => {
      return a.truck_y - b.truck_y;
    });
    //move each pallet right as much as possible
    const movedUp = [];// save pallets that are moved
    for (const pallet of pallets) {
      pallet.moveUp(movedUp);
      movedUp.push(pallet);
      await Helper.timeout(50);
      document.dispatchEvent(new Event("moved"));
    }
  }

  getTruckPallets(palletsWithProducts){
    const pallets = [];
    this.pallets.forEach(pallet => {
      const matchingPallet = palletsWithProducts.find(palletWithProducts => {
        return palletWithProducts.id === pallet.id;
      });
      pallets.push(matchingPallet);
    });
    return pallets;
  }

  getProductsCount(category, pallets) {
    let products = this.getTruckProducts(pallets);
    products = this.getCategoryProducts(category, products);
    return products.length;
  }

  getProductsArea(category, pallets) {
    let products = this.getTruckProducts(pallets);
    products = this.getCategoryProducts(category, products);
    let area = 0;
    for (const product of products) {
      area += product.area;
    }
    return parseFloat(`${area}`).toFixed(2);
  }

  getProductsWeight(category, pallets) {
    let products = this.getTruckProducts(pallets);
    products = this.getCategoryProducts(category, products);
    let weight = 0;
    for (const product of products) {
      weight += product.weight;
    }
    return weight;
  }

  getCategoryProducts(category, products) {
    return products.filter(product => {
      if (category == null) {
        return true;
      }
      return product.category == category;
    });
  }

  getTruckPallets(pallets){
    return pallets.filter(pallet => {
      return pallet.truck_id == this.id;
    });
  }

  getTruckProducts(pallets) {
    let products = [];
    const truckPallets = this.getTruckPallets(pallets);
    for (const pallet of truckPallets) {
      products = [...products, ...pallet.products];
    }
    return products;
  }
}
